"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _props = require("../utils/props");
var _pressability = require("../utils/pressability");
var _utils = require("../utils");
var _InlinePressable = _interopRequireDefault(require("./InlinePressable"));
var _ThemeProvider = require("../ThemeProvider");
var _Icon = require("../Icon");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _props.selectSystemProps)([_props.a11yProps, _props.linkProps, _props.viewProps, _props.contentfulProps]);
const selectOuterBorderStyles = _ref => {
  let {
    outerBorderColor,
    outerBorderWidth,
    outerBorderGap,
    borderRadius,
    outerBorderOutline
  } = _ref;
  return {
    outline: outerBorderOutline,
    ...(0, _ThemeProvider.applyOuterBorder)({
      outerBorderColor,
      outerBorderWidth,
      outerBorderGap
    }),
    borderRadius
  };
};
const selectTextStyles = _ref2 => {
  let {
    color,
    blockFontSize
  } = _ref2;
  return {
    color,
    ...(0, _ThemeProvider.applyTextStyles)({
      blockFontSize
    }),
    ..._Platform.default.select({
      web: {
        // TODO: https://github.com/telus/universal-design-system/issues/487
        transition: 'color 200ms'
      }
    })
  };
};
const selectBlockStyles = (_ref3, themeOptions) => {
  let {
    blockFontWeight,
    blockFontSize,
    blockLineHeight,
    blockFontName
  } = _ref3;
  return (0, _ThemeProvider.applyTextStyles)({
    lineHeight: blockLineHeight,
    fontWeight: blockFontWeight,
    fontSize: blockFontSize,
    fontName: blockFontName,
    themeOptions
  });
};
const selectDecorationStyles = _ref4 => {
  let {
    color,
    textLine,
    textLineStyle,
    alignSelf
  } = _ref4;
  return {
    color,
    textDecorationLine: textLine,
    textDecorationStyle: textLineStyle,
    alignSelf,
    ..._Platform.default.select({
      web: {
        // TODO: https://github.com/telus/universal-design-system/issues/487
        transition: 'color 200ms'
      }
    })
  };
};
const selectIconTokens = _ref5 => {
  let {
    color,
    iconSize,
    blockFontSize,
    iconTranslateX,
    iconTranslateY
  } = _ref5;
  /**
   * These calculations were carried out using a set of linear equations to calculate that the
   * position of the icon "->"" is aligned to the first line of the tooltip text.
   * The base equation is: X/4 + Y/4 - 4 - |X - Y| = Z
   * where X = blockFontSize, Y = iconSize and Z = translateY
   */
  const translateY = iconTranslateY && iconTranslateY !== 0 ? iconTranslateY : blockFontSize / 4 + iconSize / 4 - 4 - Math.abs(iconSize - blockFontSize);
  return {
    color,
    translateX: iconTranslateX,
    translateY,
    size: iconSize
  };
};

/**
 * Renders a pressable text link, with optional icon. This is rendered as a block element
 * only (see 'Current Limitations'). On Web, renders an `<a>` anchor tag if `href` is provided.
 *
 * ## Current limitations
 *
 * - Not yet integrated with widely-used Telus navigation packages. This will probably
 *   require adding a `navigate` prop that aliases onPress so this can be passed to
 *   router's Link components' `Component` or `as` props but this needs testing
 *   as part of https://github.com/telus/universal-design-system/issues/24
 *
 * - Doesn't work inline e.g. in paragraphs. Support for inline links in native and
 *   web was implemented in https://github.com/telus/universal-design-system/pull/233
 *   but removed and will need to be restored at some point. When inline link support
 *   is restored, icons in inline links on Android will be slightly off-centre
 *   vertically (see )
 *
 * - Doesn't support :visited styles on web. Support for :visited styles was
 *   implemented in https://github.com/telus/universal-design-system/pull/234 but
 *   dropped in favour of investigating if a full-featured CSS-in-JS package could or
 *   should be used more widely (e.g. styled components)
 */
const LinkBase = /*#__PURE__*/_react.default.forwardRef((_ref6, ref) => {
  let {
    href,
    icon,
    iconPosition = icon ? 'left' : undefined,
    iconProps = {},
    variant,
    tokens = {},
    children,
    dataSet,
    accessibilityRole = 'link',
    ...rawRest
  } = _ref6;
  const {
    onPress,
    ...props
  } = _props.clickProps.toPressProps(rawRest);
  const {
    hrefAttrs,
    rest
  } = _props.hrefAttrsProp.bundle(props);
  const selectedProps = selectProps({
    accessibilityRole,
    href,
    onPress: _props.linkProps.handleHref({
      href,
      onPress
    }),
    hrefAttrs,
    ...rest
  });
  const resolveLinkTokens = pressState => (0, _pressability.resolvePressableTokens)(tokens, pressState, {
    iconPosition
  });
  const defaultThemeTokens = resolveLinkTokens({});
  const hasIcon = Boolean(icon || defaultThemeTokens.icon);

  // On web, this makes focus rings wrap only the link, not the entire block
  const blockLeftStyle = _Platform.default.OS === 'web' && staticStyles.blockLeft;
  const {
    themeOptions
  } = (0, _ThemeProvider.useTheme)();
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_InlinePressable.default, {
    ...selectedProps,
    dataSet: dataSet,
    inlineFlex: hasIcon
    // assuming links without icons should be inline (even if they are long)
    ,
    ref: ref,
    style: linkState => {
      const themeTokens = resolveLinkTokens(linkState);
      const outerBorderStyles = selectOuterBorderStyles(themeTokens);
      const decorationStyles = selectDecorationStyles(themeTokens);
      return [outerBorderStyles, staticStyles.outerBorderStyles, blockLeftStyle, decorationStyles, hasIcon && staticStyles.rowContainer];
    },
    children: linkState => {
      const themeTokens = resolveLinkTokens(linkState);
      const textStyles = selectTextStyles(themeTokens);
      const iconTokens = selectIconTokens(themeTokens);
      // TODO: may need to apply some smarter text inheritance here if inline to avoid native
      // issues like double-application of line heights breaking align-items: baseline
      const blockTextStyles = selectBlockStyles(themeTokens, themeOptions);
      const IconComponent = icon || themeTokens.icon;
      const {
        iconSpace
      } = themeTokens;
      return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.IconText, {
        icon: IconComponent,
        iconPosition: iconPosition,
        space: iconSpace,
        iconProps: {
          ...iconProps,
          tokens: iconTokens,
          style: staticStyles.bubblePointerEvents
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
          style: [textStyles, blockTextStyles, staticStyles.baseline, staticStyles.bubblePointerEvents],
          children: typeof children === 'function' ? children(linkState) : children
        })
      });
    }
  });
});
LinkBase.displayName = 'LinkBase';
LinkBase.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _props.getTokensPropType)('Link'),
  /**
   * href for the Link
   */
  href: _propTypes.default.string,
  /**
   * AccesibilityRole for the link base
   */
  accessibilityrole: _propTypes.default.string,
  /**
   * Children nodes that can be added
   */
  variant: _props.variantProp.propType,
  /**
   * Optional variant that may be passed down to the link's icon if there is one
   */
  iconProps: _propTypes.default.exact(_Icon.iconComponentPropTypes),
  /**
   * A function component for an SVG icon to render inside the link. Inherits size and color from
   * the link and any Typography the link is nested inside.
   */
  icon: _propTypes.default.elementType,
  /**
   * When `icon` is provided, use `iconPosition` to place the Icon to the left, right or inline with Link.
   */
  iconPosition: _propTypes.default.oneOf(['left', 'right', 'inline']),
  /**
   * On Web if href is passed, React Native Web maps this object's props to
   * `rel`, `target` and `download` attrs.
   */
  hrefAttrs: _propTypes.default.shape(_props.hrefAttrsProp.types)
};
const staticStyles = _StyleSheet.default.create({
  blockLeft: {
    alignSelf: 'flex-start'
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  baseline: {
    alignSelf: 'baseline'
  },
  bubblePointerEvents: {
    ..._Platform.default.select({
      web: {
        pointerEvents: 'none'
      }
    })
  },
  outerBorderStyles: {
    ...(_Platform.default.OS !== 'web' && {
      margin: 0,
      marginHorizontal: 2,
      padding: 0
    }),
    ...(_Platform.default.OS === 'android' && {
      paddingHorizontal: 2,
      paddingTop: 2
    })
  }
});
var _default = exports.default = (0, _utils.withLinkRouter)(LinkBase);